/*
 * RETIRED FROM THE LIVE SCHEDULE PATH.
 *
 * The backend migration this file's original header called for has shipped:
 * GET /api/v2/views/schedule composes the Daily Schedule server-side
 * (scoping, matching, no-show classification, cross-source deduping and
 * bucketing — see calliope-backend src/services/views/scheduleView.ts, a 1:1
 * port of buildPlannerSchedule below). usePlannerSchedule makes that ONE
 * call and maps rows through plannerScheduleView.ts (presentation only).
 *
 * buildPlannerSchedule and its helpers are kept as the reference
 * implementation the server port mirrors and for their test coverage
 * (plannerScheduleAdapter.test.ts guards the semantics the backend must
 * preserve). EMPTY_PLANNER_SCHEDULE and the date/time re-exports remain in
 * live use. Do not add new live callers of buildPlannerSchedule.
 */

import {wallClockInTimeZoneToDate} from '../../utils/appointmentStatus';
import {patientNameFromRecord} from '../../utils/patientNames';
import {
  ENCOUNTER_STATUS,
  isEncounterRecording,
  isPhoneEncounter,
  isTelehealthEncounter,
  resolveEncounterModality,
} from '../../services/encounterUtils';
import {clean} from './plannerModels';
import {
  facilityDateString,
  formatPlannerFacilityTime,
  getSystemTimeZone,
} from './plannerDateTime';
import {
  encounterDetailLine,
  encounterDisplayDate,
  encounterLanguage,
  encounterRecordedMinutes,
  isPlaceholderEncounterSummary,
  representativeEncounterSession,
} from './plannerEncounterMetadata';

export {facilityDateString, getSystemTimeZone};

const SCHEDULE_GROUP_KEYS = [
  'completed',
  'noShows',
  'incomplete',
  'upcoming',
  'later',
];

const emptyScheduleGroups = () =>
  Object.fromEntries(SCHEDULE_GROUP_KEYS.map(key => [key, []]));

export const EMPTY_PLANNER_SCHEDULE = Object.freeze(emptyScheduleGroups());

const NO_SHOW_STATUSES = new Set(['no-show', 'noshow', 'no_show']);
const IGNORED_APPOINTMENT_STATUSES = new Set([
  'cancelled',
  'canceled',
  'rescheduled',
]);
const normalizeStatus = (value: any) =>
  String(value || '')
    .trim()
    .toLowerCase();
const compact = (values: any) => values.map(clean).filter(Boolean);

function normalizeStartString(appt: any) {
  const raw = clean(appt?.appointmentDate);
  if (!raw) return null;
  if (/[T ]\d{2}:\d{2}/.test(raw)) return raw;
  return `${raw} ${clean(appt?.appointmentTime) || '00:00:00'}`;
}

function appointmentStart(appt: any, timeZone: any) {
  const value = normalizeStartString(appt);
  return value ? wallClockInTimeZoneToDate(value, timeZone) : null;
}

function sameFacilityDate(date: any, targetDateKey: any, timeZone: any) {
  if (!date || !targetDateKey) return false;
  return facilityDateString(date, timeZone) === targetDateKey;
}

function patientFullName(patient: any) {
  if (!patient) return '';
  const name = patientNameFromRecord(patient);
  return name.displayName || name.fullName || name.patientName;
}

function patientLanguage(patient: any) {
  return (
    clean(patient?.preferred_dialect) ||
    clean(patient?.preferred_language) ||
    clean(patient?.language) ||
    ''
  );
}

function languageLabel(...values: any[]) {
  return values.map(clean).find(Boolean) || '';
}

function titleCase(value: any) {
  return clean(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());
}

function joinDetails(...values: any[]) {
  return compact(values).join(' · ');
}

function appointmentPatientId(appt: any) {
  return clean(appt?.patientId || appt?.ehrPatientId || appt?.ehr_patient_id);
}

function appointmentDedupeKey(appt: any, startDate: any) {
  const patientId = appointmentPatientId(appt);
  if (patientId && startDate)
    return `patient:${patientId}:at:${startDate.toISOString()}`;
  return `appointment:${appt?.id || `${patientId || 'unknown'}:${startDate?.toISOString() || 'no-time'}`}`;
}

function sourceDedupeKey(sourceType: any, sourceId: any) {
  return `${sourceType}:${sourceId || 'unknown'}`;
}

function encounterPatientKeys(encounter: any) {
  return [
    clean(encounter?.ehr_patient_id),
    clean(encounter?.patient_id),
    clean(encounter?.patient_name).toLowerCase(),
  ].filter(Boolean);
}

function encounterMatchesAppointment(
  encounter: any,
  appt: any,
  startDate: any,
  targetDateKey: any,
  timeZone: any,
) {
  if (!sameFacilityDate(startDate, targetDateKey, timeZone)) return false;
  const apptKeys = [
    appointmentPatientId(appt),
    clean(appt?.patientName).toLowerCase(),
  ].filter(Boolean);
  if (!apptKeys.length) return false;
  const encounterKeys = encounterPatientKeys(encounter);
  return apptKeys.some(key => encounterKeys.includes(key));
}

function isNoShowAppointment(appt: any) {
  return NO_SHOW_STATUSES.has(normalizeStatus(appt?.status));
}

function shouldIgnoreAppointment(appt: any) {
  return IGNORED_APPOINTMENT_STATUSES.has(normalizeStatus(appt?.status));
}

function appointmentStatusLabel(appt: any, section: any) {
  if (section === 'noShows') return 'No-show';
  const status = titleCase(appt?.status);
  return status && status !== 'Scheduled' ? status : 'Appointment';
}

function appointmentDetailLine(appt: any) {
  return joinDetails(appt?.providerName, appt?.facility, appt?.reason);
}

// Common envelope shared by every Daily Schedule row regardless of source.
// Source builders override only the fields their source actually knows; the
// raw source doc (appointment/encounter/session/scheduledBot) rides along so
// plannerModels can translate it into PlannerEvent actions. The card
// time label always derives from sortDate in the requested display timezone.
function buildScheduleItem({
  displayTimeZone,
  ...overrides
}: Record<string, any>) {
  const item = {
    id: '',
    sourceType: 'unknown',
    sourceId: null,
    dedupeKey: '',
    patientName: '',
    languageDialect: '',
    sourceLabel: '',
    modalityLabel: '',
    statusLabel: '',
    accentKey: 'default',
    detailLine: '',
    conversationSummary: '',
    isTelehealth: false,
    durationLabel: '',
    sortDate: null,
    ...overrides,
  };

  return {
    ...item,
    patientName: clean(item.patientName) || 'Unknown patient',
    time: formatPlannerFacilityTime(item.sortDate, displayTimeZone),
  };
}

function buildAppointmentItem({
  appt,
  patient,
  startDate,
  section,
  displayTimeZone,
}: Record<string, any>) {
  return buildScheduleItem({
    displayTimeZone,
    id: `${section}-appointment-${appt?.id || appointmentDedupeKey(appt, startDate)}`,
    sourceType: 'ehrAppointment',
    sourceId: appt?.id || null,
    dedupeKey: appointmentDedupeKey(appt, startDate),
    patientName: clean(appt?.patientName) || patientFullName(patient),
    languageDialect: languageLabel(
      patientLanguage(patient),
      appt?.language,
      appt?.dialect,
    ),
    sourceLabel: 'EHR',
    statusLabel: appointmentStatusLabel(appt, section),
    accentKey: section === 'noShows' ? 'noShow' : 'ehr',
    detailLine: appointmentDetailLine(appt),
    appointment: appt,
    ehrPatient: patient || null,
    sortDate: startDate,
  });
}

function buildEncounterItem({
  encounter,
  section,
  displayTimeZone,
  viewerUserId,
}: Record<string, any>) {
  const summary = isPlaceholderEncounterSummary(encounter?.summary)
    ? ''
    : clean(encounter?.summary);
  const modality = resolveEncounterModality(encounter);
  const durationMinutes = encounterRecordedMinutes(encounter);

  return buildScheduleItem({
    displayTimeZone,
    id: `${section}-encounter-${encounter?.id}`,
    sourceType: encounter?.isSynthetic ? 'conversation' : 'encounter',
    sourceId: encounter?.id || null,
    dedupeKey: sourceDedupeKey('encounter', encounter?.id),
    patientName: encounter?.patient_name,
    languageDialect: encounterLanguage(encounter),
    sourceLabel: modality.badge,
    modalityLabel: modality.badge,
    durationLabel: durationMinutes > 0 ? `${durationMinutes} min` : '',
    accentKey: modality.isScribe ? 'scribe' : 'interpreter',
    // Phone wins over telehealth: web dial-out encounters carry both signals
    // (isTelephonic + a telehealth workflow) but were delivered by phone.
    isTelephonic: isPhoneEncounter(encounter),
    isTelehealth:
      !isPhoneEncounter(encounter) && isTelehealthEncounter(encounter),
    detailLine: encounterDetailLine(encounter, viewerUserId),
    conversationSummary: summary,
    encounter,
    session: representativeEncounterSession(encounter, viewerUserId),
    sortDate: encounterDisplayDate(encounter),
    ...(section === 'incomplete' ? {elapsedMinutes: durationMinutes} : {}),
  });
}

function botDate(bot: any) {
  const value =
    bot?.join_at || bot?.joinAt || bot?.startedAt || bot?.created_at;
  if (!value) return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function botDedupeKey(bot: any, startDate: any) {
  const patientId = clean(
    bot?.ehrPatientId ||
      bot?.ehr_patient_id ||
      bot?.localPatientId ||
      bot?.patient_id,
  );
  if (patientId && startDate)
    return `patient:${patientId}:at:${startDate.toISOString()}`;
  return sourceDedupeKey('scheduledBot', bot?.id);
}

function botModality(bot: any) {
  const mode = normalizeStatus(bot?.mode || bot?.bot_mode);
  const workflow = clean(bot?.workflow_type || bot?.workflowType);
  return resolveEncounterModality(
    mode === 'scribe'
      ? 'telehealth_scribe'
      : workflow || 'telehealth_interpretation',
  );
}

function botDetailLine(bot: any) {
  return joinDetails(
    bot?.providerName || bot?.provider_name,
    bot?.meetingHost,
    bot?.durationLabel,
  );
}

function buildScheduledBotItem({
  bot,
  startDate,
  section = 'upcoming',
  displayTimeZone,
}: Record<string, any>) {
  const modality = botModality(bot);
  return buildScheduleItem({
    displayTimeZone,
    id: `${section}-scheduled-bot-${bot?.id}`,
    sourceType: 'scheduledBot',
    sourceId: bot?.id || null,
    dedupeKey: botDedupeKey(bot, startDate),
    patientName: bot?.patientName || bot?.patient_name,
    languageDialect: languageLabel(bot?.dialect, bot?.language),
    sourceLabel: 'Scheduled bot',
    modalityLabel: modality.badge,
    statusLabel: 'Scheduled',
    accentKey: modality.isScribe ? 'scribe' : 'interpreter',
    isTelehealth: true,
    detailLine: botDetailLine(bot),
    conversationSummary: clean(bot?.summary),
    scheduledBot: bot,
    sortDate: startDate,
  });
}

function sortItems(items: any) {
  return [...items]
    .sort((a, b) => {
      const ta = a.sortDate?.getTime?.() ?? Number.POSITIVE_INFINITY;
      const tb = b.sortDate?.getTime?.() ?? Number.POSITIVE_INFINITY;
      if (ta !== tb) return ta - tb;
      return String(a.patientName || '').localeCompare(
        String(b.patientName || ''),
      );
    })
    .map(({dedupeKey, ...item}) => item);
}

function addUnique(sectionItems: any, item: any, seen: any) {
  const key = item.dedupeKey || sourceDedupeKey(item.sourceType, item.sourceId);
  if (seen.has(key)) return;
  seen.add(key);
  sectionItems.push(item);
}

export function buildPlannerSchedule({
  appointments = [],
  patientsById = {},
  encounters = [],
  scheduledBots = [],
  now = new Date(),
  timeZone = 'America/New_York',
  displayTimeZone = getSystemTimeZone(),
  targetDateKey = facilityDateString(now, timeZone),
  viewerUserId = null,
}: Record<string, any> = {}) {
  const schedule = emptyScheduleGroups();
  const seenBySection = Object.fromEntries(
    SCHEDULE_GROUP_KEYS.map(key => [key, new Set()]),
  );

  const todaysEncounters = encounters.filter((encounter: any) => {
    const date = encounterDisplayDate(encounter);
    return sameFacilityDate(date, targetDateKey, timeZone);
  });

  for (const encounter of todaysEncounters) {
    const section =
      isEncounterRecording(encounter) ||
      encounter?.status === ENCOUNTER_STATUS.OPEN
        ? 'incomplete'
        : 'completed';
    addUnique(
      schedule[section],
      buildEncounterItem({encounter, section, displayTimeZone, viewerUserId}),
      seenBySection[section],
    );
  }

  for (const bot of scheduledBots) {
    const startDate = botDate(bot);
    if (!startDate || startDate.getTime() <= now.getTime()) continue;
    const section = sameFacilityDate(startDate, targetDateKey, timeZone)
      ? 'upcoming'
      : 'later';
    addUnique(
      schedule[section],
      buildScheduledBotItem({bot, startDate, section, displayTimeZone}),
      seenBySection[section],
    );
  }

  for (const appt of appointments) {
    const startDate = appointmentStart(appt, timeZone);
    if (!sameFacilityDate(startDate, targetDateKey, timeZone)) continue;
    if (shouldIgnoreAppointment(appt)) continue;

    const patientId = appointmentPatientId(appt);
    const patient = patientId ? patientsById[patientId] : null;

    if (isNoShowAppointment(appt)) {
      addUnique(
        schedule.noShows,
        buildAppointmentItem({
          appt,
          patient,
          startDate,
          section: 'noShows',
          displayTimeZone,
        }),
        seenBySection.noShows,
      );
      continue;
    }

    if (startDate && startDate.getTime() > now.getTime()) {
      addUnique(
        schedule.upcoming,
        buildAppointmentItem({
          appt,
          patient,
          startDate,
          section: 'upcoming',
          displayTimeZone,
        }),
        seenBySection.upcoming,
      );
      continue;
    }

    // The matching encounter (active or completed) is the canonical card.
    // Keeping the appointment too would show the same patient/time twice.
    const hasMatchingEncounter = todaysEncounters.some((encounter: any) =>
      encounterMatchesAppointment(
        encounter,
        appt,
        startDate,
        targetDateKey,
        timeZone,
      ),
    );
    if (hasMatchingEncounter) continue;

    // A past appointment nobody has documented yet still belongs on today's
    // schedule, so keep it visible under Upcoming instead of dropping it.
    addUnique(
      schedule.upcoming,
      buildAppointmentItem({
        appt,
        patient,
        startDate,
        section: 'upcoming',
        displayTimeZone,
      }),
      seenBySection.upcoming,
    );
  }

  return Object.fromEntries(
    SCHEDULE_GROUP_KEYS.map(key => [key, sortItems(schedule[key])]),
  );
}
